/**
 * Unsharp mask: subtract a blurred copy from the original to isolate the
 * frequency band lost to blur, then add it back with a gain. CLAHE/
 * illumination flattening can also soften hard bar/space edges slightly, so
 * a light default-radius pass afterwards restores crispness there too.
 *
 * `radius` matters more than it looks: the reference blur has to be at
 * least as wide as the blur you're trying to undo, or there's no
 * low-frequency signal to subtract out and boost — a fixed tiny radius can
 * only ever sharpen fine, near-focus blur. `enhanceForCondition` picks a
 * radius from the frame's measured focus score for the "blurry" condition
 * instead of using the cheap default meant for post-CLAHE touch-ups.
 */
export function unsharpMask(
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  amount = 0.6,
  radius = 1,
): Uint8ClampedArray {
  const blurred = boxBlur(luma, width, height, radius);
  const out = new Uint8ClampedArray(luma.length);
  for (let i = 0; i < luma.length; i++) {
    out[i] = luma[i] + (luma[i] - blurred[i]) * amount;
  }
  return out;
}

/** Box blur with an arbitrary radius, O(width*height) via a sliding window sum per axis. */
function boxBlur(luma: Uint8ClampedArray, width: number, height: number, radius: number): Uint8ClampedArray {
  if (radius <= 1) return boxBlur3(luma, width, height);
  const tmp = new Float32Array(luma.length);
  const out = new Uint8ClampedArray(luma.length);

  // Horizontal pass.
  for (let y = 0; y < height; y++) {
    const row = y * width;
    let sum = 0;
    for (let x = -radius; x <= radius; x++) sum += luma[row + clampIndex(x, width)];
    for (let x = 0; x < width; x++) {
      tmp[row + x] = sum / (radius * 2 + 1);
      const add = luma[row + clampIndex(x + radius + 1, width)];
      const sub = luma[row + clampIndex(x - radius, width)];
      sum += add - sub;
    }
  }

  // Vertical pass.
  for (let x = 0; x < width; x++) {
    let sum = 0;
    for (let y = -radius; y <= radius; y++) sum += tmp[clampIndex(y, height) * width + x];
    for (let y = 0; y < height; y++) {
      out[y * width + x] = sum / (radius * 2 + 1);
      const add = tmp[clampIndex(y + radius + 1, height) * width + x];
      const sub = tmp[clampIndex(y - radius, height) * width + x];
      sum += add - sub;
    }
  }
  return out;
}

function clampIndex(i: number, max: number): number {
  return Math.min(Math.max(i, 0), max - 1);
}

function boxBlur3(luma: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(luma.length);
  for (let y = 0; y < height; y++) {
    const yUp = Math.max(0, y - 1) * width;
    const yMid = y * width;
    const yDown = Math.min(height - 1, y + 1) * width;
    for (let x = 0; x < width; x++) {
      const xL = Math.max(0, x - 1);
      const xR = Math.min(width - 1, x + 1);
      let sum = 0;
      sum += luma[yUp + xL] + luma[yUp + x] + luma[yUp + xR];
      sum += luma[yMid + xL] + luma[yMid + x] + luma[yMid + xR];
      sum += luma[yDown + xL] + luma[yDown + x] + luma[yDown + xR];
      out[yMid + x] = sum / 9;
    }
  }
  return out;
}
